/**
 * A tick under the finger, by whichever route the platform leaves open.
 *
 * Android answers `navigator.vibrate` from anywhere, so one listener over the
 * whole page is the whole of it, and every press can be felt.
 *
 * Safari has no vibration API and WebKit has said it does not want one. The
 * only control on iOS that reaches the Taptic Engine is a native switch, and
 * only under a real finger — scripted toggles stopped counting in iOS 26.5 —
 * so a press is made to feel by laying an invisible switch over the thing
 * being pressed. That switch can be dragged, and its drag beats the page, so
 * a finger landing on one to begin a scroll moves it instead of the screen.
 * Nothing settles that: not handing panning back with `touch-action`, not
 * taking the drawing away with `appearance`. So on iOS a surface goes only
 * where a press is a press — chrome, panels, controls off to one side — and
 * never in the middle of a screen that scrolls.
 */

declare module "react" {
  interface InputHTMLAttributes<T> {
    /**
     * Safari draws the checkbox as a native switch, which is what ticks.
     * Presence is the whole of it, and React only writes an attribute it
     * does not know about when it is given a string.
     */
    switch?: "";
  }
}

/** Short enough to read as a tick rather than a buzz. */
const TICK_MS = 8;

/** Anything a press can be felt through, which is what a tick answers. */
const PRESSABLE = "button, a[href], [role='button']";

/**
 * Only a phone feels anything, and only a phone should carry the extra
 * control: on a desktop it would swallow the modifier-clicks that open a link
 * in its own tab.
 */
const feelsTaps = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

const answersVibrate = feelsTaps && typeof navigator !== "undefined" && "vibrate" in navigator;

/** Where the vibration API is missing, the switch is the only way through. */
const needsSurface =
  feelsTaps &&
  !answersVibrate &&
  typeof document !== "undefined" &&
  "switch" in document.createElement("input");

/**
 * Give every press on the page a tick, on the platforms that will do it
 * without being handed a control to press. Call once, from the entry point.
 */
export function installHaptics(): () => void {
  if (!answersVibrate) return () => {};

  const tickOnPress = (event: MouseEvent) => {
    if ((event.target as Element | null)?.closest(PRESSABLE)) navigator.vibrate(TICK_MS);
  };

  document.addEventListener("click", tickOnPress);
  return () => document.removeEventListener("click", tickOnPress);
}

/**
 * The iOS way in: put this inside a control and give the host `relative`. It
 * covers the host and is invisible to both eye and screen reader.
 *
 * Read the note above before adding one. A surface holds the page still under
 * the finger that lands on it, so it belongs on chrome and on controls that
 * sit out of the way of a scroll, and nowhere else.
 *
 * The press itself belongs to the host: the click starts on the surface and
 * bubbles, so an `onClick` above it runs as it always did. What does not
 * survive is the browser's own idea of what a click means — the surface is
 * what got activated, so a bare `<a href>` stops navigating and a submit
 * button stops submitting. Everything here routes through `onClick`, router
 * links included, so this only rules out sending one somewhere off-site.
 */
export function HapticSurface() {
  if (!needsSurface) return null;

  return (
    <input
      type="checkbox"
      switch=""
      aria-hidden
      tabIndex={-1}
      className="absolute inset-0 m-0 size-full appearance-none touch-manipulation opacity-0 [-webkit-tap-highlight-color:transparent]"
    />
  );
}
